import React, { useState } from 'react';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { RefreshCw, Sparkles, Copy, Check, ArrowLeft } from 'lucide-react';
import LexMessageRenderer from '../../../../components/lex/LexMessageRenderer';

interface LexExplainModalProps {
  isOpen: boolean;
  title?: string;
  subtitle?: string;
  explanation?: string;
  provider?: string;
  model?: string;
  references?: Array<{
    id: string;
    kind: string;
    label: string;
    description?: string;
    deepLink?: string;
    filters: Record<string, any>;
  }>;
  referenceLoading?: boolean;
  loading?: boolean;
  error?: string | null;
  onClose: () => void;
  onRetry?: () => void;
  onReferenceClick?: (reference: any) => void;
  canBack?: boolean;
  onBack?: () => void;
}

export const LexExplainModal: React.FC<LexExplainModalProps> = ({
  isOpen,
  title = 'Lex Explanation',
  subtitle,
  explanation,
  provider,
  model,
  references,
  referenceLoading,
  loading,
  error,
  onClose,
  onRetry,
  onReferenceClick,
  canBack,
  onBack,
}) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const canCopy = !!explanation && !loading && !error;
  const isBusy = !!loading || !!referenceLoading;

  const copyToClipboard = async () => {
    if (!explanation) return;
    try {
      await navigator.clipboard.writeText(explanation);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75"
          onClick={onClose}
        />

        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full border border-gray-200 dark:border-gray-700">
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Sparkles className="w-5 h-5 text-lex-accent" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">{title}</h3>
                  {subtitle && <div className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</div>}
                </div>
              </div>

              <div className="flex items-center space-x-2">
                {provider && (
                  <Badge variant="secondary" className="hidden sm:inline-flex">
                    {provider}{model ? ` • ${model}` : ''}
                  </Badge>
                )}

                {canBack && onBack && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onBack}
                    disabled={loading || referenceLoading}
                    className="flex items-center space-x-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back</span>
                  </Button>
                )}

                {onRetry && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onRetry}
                    disabled={loading}
                    className="flex items-center space-x-2"
                  >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    <span>Retry</span>
                  </Button>
                )}

                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyToClipboard}
                  disabled={!canCopy}
                  className="flex items-center space-x-2"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  <span>{copied ? 'Copied' : 'Copy'}</span>
                </Button>

                <Button variant="outline" size="sm" onClick={onClose}>
                  Close
                </Button>
              </div>
            </div>

            <div className="min-h-[220px]">
              {error ? (
                <div className="py-6">
                  <div className="text-sm text-red-600 dark:text-red-400 font-medium">Lex failed to generate an explanation.</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">{error}</div>
                </div>
              ) : (
                <>
                  {Array.isArray(references) && references.length > 0 && (
                    <div className="mb-4">
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Evidence</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {references.map((ref) => {
                          const disabled = !!referenceLoading || !!loading;
                          return (
                            <button
                              key={ref.id}
                              type="button"
                              disabled={disabled}
                              onClick={() => {
                                if (onReferenceClick) {
                                  onReferenceClick(ref);
                                  return;
                                }
                                if (ref.deepLink) window.location.assign(ref.deepLink);
                              }}
                              title={ref.description || ref.label}
                              className={`px-2 py-1 rounded-md text-xs border transition-colors ${
                                disabled
                                  ? 'opacity-60 cursor-not-allowed'
                                  : 'hover:bg-lex-state-hover hover:border-lex-accent'
                              }`}
                            >
                              {ref.label}
                            </button>
                          );
                        })}
                      </div>
                      {referenceLoading && (
                        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                          <span className="inline-flex items-center gap-2">
                            <RefreshCw className="w-3 h-3 animate-spin" />
                            Loading evidence…
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {isBusy ? (
                    <div className="flex items-center justify-center py-10">
                      <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-300">
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>{loading ? 'Lex is analyzing this…' : 'Gathering data…'}</span>
                      </div>
                    </div>
                  ) : (
                    <LexMessageRenderer
                      content={explanation || 'No explanation returned.'}
                      role="assistant"
                      enableLegalFormatting={false}
                      enableSmartEmojis={true}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
